import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { japanPrefectures } from '../mocks/japanPrefectures';

export interface PrefectureShop {
  id: string;
  userId: string;
  name: string;
  category: string;
  description: string;
  address: string;
  lat: number;
  lng: number;
  color: string;
  thumbnail: string;
  attributeId?: string;
  mainGenreId?: string;
  mainPartId?: string;
  prefecture: string;
  websiteUrl: string;
  phone: string;
  businessHours: string;
}

/**
 * 都道府県IDからSupabaseに登録されたショップを取得するフック
 */
export function usePrefectureShops(prefectureId: string) {
  const [shops, setShops] = useState<PrefectureShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const fetchShops = async () => {
      setLoading(true);
      try {
        const prefData = japanPrefectures.find((p) => p.id === prefectureId);
        if (!prefData) {
          setShops([]);
          setCount(0);
          setLoading(false);
          return;
        }

        // 「奈良」「奈良県」両方にマッチするよう ilike で検索
        const prefName = prefData.name; // 例: "奈良県"
        const prefShort = prefName.replace(/[都道府県]$/, ''); // 例: "奈良"

        const { data, error } = await supabase
          .from('shop_layers')
          .select(
            'id, user_id, shop_name, category, address, description, color, thumbnail, lat, lng, prefecture, website_url, phone, business_hours, attribute_id, main_genre_id, main_part_id'
          )
          .or(`prefecture.eq.${prefName},prefecture.eq.${prefShort}`);

        if (error) throw error;

        if (!data || data.length === 0) {
          setShops([]);
          setCount(0);
          setLoading(false);
          return;
        }

        const result: PrefectureShop[] = data.map((row) => ({
          id: row.id,
          userId: row.user_id,
          name: row.shop_name || '',
          category: row.category || 'その他',
          address: row.address || '',
          description: row.description || '',
          color: row.color || '#00ff41',
          thumbnail: row.thumbnail || '',
          lat: row.lat ?? null,
          lng: row.lng ?? null,
          prefecture: row.prefecture || '',
          websiteUrl: row.website_url || '',
          phone: row.phone || '',
          businessHours: row.business_hours || '',
          attributeId: row.attribute_id || undefined,
          mainGenreId: row.main_genre_id || undefined,
          mainPartId: row.main_part_id || undefined,
        }));

        setShops(result);
        setCount(result.length);
      } catch (err) {
        console.error('Failed to fetch prefecture shops:', err);
        setShops([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchShops();
  }, [prefectureId]);

  return { shops, count, loading };
}
